import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import Skeleton from "components/Skeleton";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import BlogCard, { PostSkeleton } from "./BlogCard";
import { shuffleArray } from "utils";

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const BLOG_ID_SECURE = process.env.REACT_APP_GOOGLE_BLOG_ID_SECURE;
const BLOG_ID_ADHERE = process.env.REACT_APP_GOOGLE_BLOG_ID_ADHERE;
const BLOG_ID_INTEL = process.env.REACT_APP_GOOGLE_BLOG_ID_INTEL;

const AllPosts = ({ search, save }) => {
  const { t } = useTranslation("home");

  const [posts, setPosts] = useState({ secure: [], adhere: [], intel: [] });

  const [loading, setLoading] = useState(true);
  const [itemOffset, setItemOffset] = useState(0);

  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    "All Posts",
    "Seequre",
    "Adhere",
    "Oculus",
    "Smartcomply Academy",
  ];

  const OFFSET = 3;

  const modifiedPosts = (data, category = null) => {
    if (!Array.isArray(data)) return data;
    return data?.map((post) => {
      const { replies, ...others } = post;
      return {
        ...others,
        labels: post?.labels?.map((label) => label?.toLowerCase()) || [],
        ...(category ? { category } : {}),
      };
    });
  };

  const getAllPosts = async () => {
    setLoading(true);
    try {
      const secure_response = await fetch(
        `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID_SECURE}/posts?key=${API_KEY}`,
      );
      const adhere_response = await fetch(
        `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID_ADHERE}/posts?key=${API_KEY}`,
      );
      const intel_response = await fetch(
        `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID_INTEL}/posts?key=${API_KEY}`,
      );
      setLoading(false);
      const secure_data = await secure_response.json();
      const adhere_data = await adhere_response.json();
      const intel_data = await intel_response.json();

      if (
        secure_response.status === 200 ||
        adhere_response.status === 200 ||
        intel_response.status === 200
      ) {
        // if (
        //   ![...secure_data.items, ...adhere_data.items, ...intel_data.items]
        //     .length
        // )
        //   setNoPosts(true);

        setPosts({
          secure: modifiedPosts(secure_data?.items, "secure"),
          adhere: modifiedPosts(adhere_data?.items, "adhere"),
          intel: modifiedPosts(intel_data?.items, "intel"),
        });
      }
    } catch (error) {
      setLoading(false);
      toast.error(`${t("toast.posts")}`);
    }
  };

  const filtered_posts = useMemo(() => {
    let filteredItems = shuffleArray([
      ...posts.adhere,
      ...posts.secure,
      ...posts.intel,
    ]);

    if (activeTab !== 0) {
      filteredItems =
        activeTab === 0
          ? filteredItems
          : activeTab === 1
            ? posts.secure
            : activeTab === 2
              ? posts.adhere
              : activeTab === 3
                ? posts.intel
                : [];
    }

    if (search && save) {
      filteredItems = filteredItems?.filter((post) =>
        post.title?.toLowerCase()?.includes(search),
      );
    }

    return filteredItems;
  }, [posts, activeTab, search, save]);

  const mostRecentPosts = useMemo(() => {
    let data = shuffleArray([...posts.adhere, ...posts.secure, ...posts.intel]);

    if (data?.length === 0) return [];

    if (search && save) {
      data = data?.filter((post) =>
        post.title?.toLowerCase()?.includes(search),
      );
    }

    return data
      ?.sort((a, b) => {
        return moment(b.published).isAfter(moment(a.published)) ? 1 : -1;
      })
      ?.slice(0, 3);
  }, [posts, search, save]);

  useEffect(() => {
    getAllPosts();
  }, []);

  useEffect(() => {
    setItemOffset(0);
  }, [activeTab]);

  const endOffset = itemOffset + OFFSET;
  const currentPosts = [...filtered_posts].slice(itemOffset, endOffset);
  const pageCount = Math.ceil(filtered_posts.length / OFFSET);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * OFFSET) % filtered_posts.length;
    setItemOffset(newOffset);
  };

  return (
    <>
      <section className=" px-[20%] max-md:px-4 max-[520px]:px-4 max-lg:pt-8 max-md:pt-8 bg-white">
        <section className="flex flex-col justify-center items-center gap-12 max-md:gap-6">
          <div className="relative w-full md:hidden"></div>
        </section>
      </section>
      <section className=" px-14 py-14 max-md:px-8 max-[520px]:px-4 max-lg:py-14 max-md:py-8 bg-white">
        <>
          <div className="w-full mb-3 flex items-center justify-between gap-5 max-md:items-start">
            {loading ? (
              <div className="flex gap-10 items-center justify-between w-full">
                <div className="">
                  <div className="hidden gap-3 md:flex">
                    <Skeleton styles="w-64 h-10 rounded-3xl" />
                  </div>
                </div>
              </div>
            ) : mostRecentPosts?.length ? (
              <>
                <h4 className="font-semibold text-left text-xl capitalize font-lexend">
                  {t(`blog.recentHeader`)}
                </h4>
              </>
            ) : null}
          </div>

          <div className="w-full">
            {loading ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-[570px]:grid-cols-1">
                  <PostSkeleton key={"recentskeleton" + 0} />
                  <div className="flex flex-col gap-4">
                    <PostSkeleton key={"recentskeleton" + 1} recent />
                    <PostSkeleton key={"recentskeleton" + 2} recent />
                  </div>
                </div>
              </>
            ) : (
              <>
                {mostRecentPosts?.length ? (
                  <>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-[570px]:grid-cols-1">
                      {mostRecentPosts.map((card, index) => (
                        <BlogCard
                          key={index}
                          post={card}
                          recent
                          category={card.category}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        </>

        <section className="w-full flex flex-col gap-0 max-md:gap-10 justify-center items-center mt-20 max-md:mt-8">
          <div className="hidden md:block no-scrollbar w-full overflow-y-auto whitespace-nowrap mx-auto text-left justify-between items-start gap-2 text-[15px]">
            {tabs.map((tab, key) => (
              <div
                key={tab.key}
                className={`${
                  activeTab === key
                    ? "text-black font-euclidSemibold border-b-2 border-[#202D66]"
                    : "text-[#00000066] font-euclidRegular cursor-pointer"
                } px-3 py-2 inline-flex min-w-[90px] justify-center`}
                onClick={() => setActiveTab(key)}
              >
                {tab?.toLowerCase() === "all" ? t(`blog.all`) : tab}
              </div>
            ))}
          </div>
          <div className="flex flex-col w-full mt-10">
            <div className="w-full flex items-center justify-between gap-5 max-md:items-start">
              {loading ? (
                <div className="flex gap-10 items-center justify-between w-full">
                  <div className="">
                    <div className="hidden gap-3 md:flex">
                      <Skeleton styles="w-64 h-10 rounded-3xl" />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <h4 className="font-bold text-left text-xl capitalize">
                    {tabs[activeTab]?.toLowerCase() === "all"
                      ? t(`blog.all`)
                      : tabs[activeTab]}{" "}
                    {t(`blog.postsTitle`)}
                  </h4>
                </>
              )}
            </div>

            <div className="w-full mt-6 max-md:mt-8">
              {loading ? (
                <div className="grid grid-cols-3 max-[920px]:grid-cols-2 max-[570px]:grid-cols-1 gap-6 max-[900px]:gap-5">
                  {Array(3)
                    .fill(0)
                    .map((_arg, index) => (
                      <PostSkeleton key={"skeleton" + index} />
                    ))}
                </div>
              ) : (
                <>
                  {currentPosts?.length ? (
                    <div className="grid grid-cols-3 max-[920px]:grid-cols-2 max-[570px]:grid-cols-1 gap-6 max-[900px]:gap-5">
                      {currentPosts.map((card, index) => (
                        <BlogCard
                          key={index}
                          post={card}
                          category={card.category}
                        />
                      ))}
                    </div>
                  ) : (
                    <h4 className="my-8 flex justify-center items-center capitalize font-extrabold text-black text-xl md:text-2xl">
                      {t("blog.noPosts")}
                    </h4>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
        <ReactPaginate
          breakLabel="..."
          nextLabel={
            <div className="flex gap-2 items-center justify-end max-[400px]:flex-col-reverse max-[400px]:items-end">
              <p className="text-[14px] text-[#667085] font-[Inter] capitalize">
                {t(`blog.next`)}
              </p>
              <ArrowRightOutlined className="text-[#667085] font-[Inter] text-[14px]" />
            </div>
          }
          onPageChange={handlePageClick}
          // pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel={
            <div className="flex gap-2 items-center justify-start max-[400px]:flex-col max-[400px]:items-start">
              <ArrowLeftOutlined className="text-[#667085] font-[Inter] text-[14px]" />
              <p className="text-[14px] text-[#667085] font-[Inter] capitalize">
                {t(`blog.previous`)}
              </p>
            </div>
          }
          renderOnZeroPageCount={null}
          className="border-t border-[#EAECF0] pt-8 mt-8 w-full flex items-center justify-start"
          pageClassName="text-[#667085] px-4 py-1.5 w-fit  text-[14px]"
          activeClassName="bg-[#F9F5FF] rounded-[10px]"
          containerClassName=""
          previousClassName="w-fit flex-1"
          nextClassName="w-fit flex-1"
        />
      </section>
    </>
  );
};

export default AllPosts;
